import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportCustomerLtv } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function CustomerLtvReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-customer-ltv', qBuilt],
    queryFn: () => fetchReportCustomerLtv(qBuilt),
  });

  const d = q.data;

  const chartRows = useMemo(
    () => (d?.chart ?? []).map((r) => ({ ...r, avgLtvNum: Number(r.avgLtv) })),
    [d],
  );

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.topCustomers.map((c) => ({
        name: c.name,
        totalSpend: c.totalSpend,
        totalTickets: c.totalTickets,
        lastVisitAt: c.lastVisitAt,
        ltv: c.ltv,
      })),
      'customer-ltv.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading customer lifetime value…</div>;
  if (q.isError || !d?.summary || !d.topCustomers || !d.segments) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load customer LTV report.</div>;
  }

  const s = d.summary;

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Avg lifetime value', v: formatCurrency(s.avgLtv) },
          { label: 'Avg orders / customer', v: s.avgOrdersPerCustomer.toFixed(1) },
          { label: 'Avg order value', v: formatCurrency(s.avgOrderValue) },
          { label: 'Total customers', v: s.totalCustomers },
          { label: 'Repeat rate', v: `${s.repeatRate.toFixed(1)}%` },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Customer segments</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="py-2">Segment</th>
                  <th className="py-2">Customers</th>
                  <th className="py-2">Avg LTV</th>
                  <th className="py-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {d.segments.map((seg) => (
                  <tr key={seg.segment} className="border-t border-slate-100">
                    <td className="py-2 font-medium capitalize">{seg.segment.replace(/_/g, ' ')}</td>
                    <td className="py-2">{seg.customerCount}</td>
                    <td className="py-2">{formatCurrency(seg.avgLtv)}</td>
                    <td className="py-2">{formatCurrency(seg.totalRevenue)}</td>
                  </tr>
                ))}
                {d.segments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-slate-500">No segment data yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">LTV by cohort</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="cohort" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="avgLtvNum" name="Avg LTV" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Total spend</th>
              <th className="px-4 py-3">Tickets</th>
              <th className="px-4 py-3">Last visit</th>
              <th className="px-4 py-3">LTV</th>
            </tr>
          </thead>
          <tbody>
            {d.topCustomers.map((c) => (
              <tr key={c.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-medium">
                  <Link to={`/customers/${c.id}`} className="text-primary hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-2">{formatCurrency(c.totalSpend)}</td>
                <td className="px-4 py-2">{c.totalTickets}</td>
                <td className="px-4 py-2">{formatDate(c.lastVisitAt)}</td>
                <td className="px-4 py-2 font-semibold">{formatCurrency(c.ltv)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.topCustomers.length ? <p className="p-6 text-center text-slate-500">No customer activity in this period.</p> : null}
      </div>
    </div>
  );
}
